import { c as _c } from "react/compiler-runtime";
import { CheckCircle2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import { Button } from "@shared-components/shadcn/button";
import { Checkbox } from "@shared-components/shadcn/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@shared-components/shadcn/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@shared-components/shadcn/form";
import { Textarea } from "@shared-components/shadcn/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared-components/shadcn/tooltip";
import SimpleFormField from "@shared-components/simple-form-field";
import { Spinner } from "@shared-components/spinner";
import { api } from "@shared-server/api";
import { reportBugSchema, type ReportBugType } from "@shared-utils/validators";
import html2canvas from "html2canvas-pro";
import { CircleHelp } from "lucide-react";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
interface ReportBugFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export function ReportBugForm(props) {
  const $ = _c(24);
  const slug = usePathname();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const reportBugMutation = api.reportBug.submit.useMutation({
    onSuccess: () => {
      setIsSubmitting(false);
      form.reset();
      setShowThankYou(true);
    }
  });
  const captureScreenshot = _temp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = zodResolver(reportBugSchema);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      title: "",
      description: "",
      includeScreenshot: true,
      allowAdminAccess: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== reportBugMutation.isPending) {
    t2 = {
      resolver: t0 as Resolver<ReportBugType>,
      defaultValues: t1,
      disabled: reportBugMutation.isPending
    };
    $[2] = reportBugMutation.isPending;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const form = useForm(t2);
  let t3;
  if ($[4] !== captureScreenshot || $[5] !== form || $[6] !== reportBugMutation || $[7] !== slug) {
    t3 = async () => {
      setIsSubmitting(true);
      const formValues = form.getValues();
      const includeScreenshot = formValues.includeScreenshot;
      const screenshotData = includeScreenshot ? await captureScreenshot() : null;
      reportBugMutation.mutate({
        ...formValues,
        slug,
        screenshotData
      });
    };
    $[4] = captureScreenshot;
    $[5] = form;
    $[6] = reportBugMutation;
    $[7] = slug;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onSubmit = t3;
  let t4;
  if ($[9] !== props || $[10] !== reportBugMutation.isPending) {
    t4 = open => {
      if (reportBugMutation.isPending) {
        return;
      }
      props.setOpen(open);
    };
    $[9] = props;
    $[10] = reportBugMutation.isPending;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleOpenChange = t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DialogTitle>{"Report Bug"}</DialogTitle>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== form || $[14] !== isSubmitting || $[15] !== onSubmit || $[16] !== props || $[17] !== reportBugMutation.isPending || $[18] !== showThankYou) {
    t6 = <DialogContent className="max-w-2xl">{t5}{showThankYou ? <><div className="flex flex-col items-center justify-center gap-4"><CheckCircle2 className="h-12 w-12 text-ter-green-500" /><div className="text-lg">{"Thank you for submitting a bug report!"}</div><div className="text-center text-sm text-muted-foreground max-w-md">{"We will look into it asap and let you know when a fix is implemented or reach out to you if additional info is needed."}</div></div><DialogFooter><Button onClick={() => {
            setShowThankYou(false);
            props.setOpen(false);
          }}>{"Ok"}</Button></DialogFooter></> : <Form {...form}><form id="report-bug-form" className="flex flex-col gap-4"><SimpleFormField name="title" label="Title*" form={form} /><FormField control={form.control} name="description" render={_temp2} /><FormField control={form.control} name="includeScreenshot" render={_temp3} /><FormField control={form.control} name="allowAdminAccess" render={_temp4} /><DialogFooter>{reportBugMutation.isPending && <div className="mt-2 mr-4"><Spinner /></div>}<Button variant="secondary" type="button" disabled={reportBugMutation.isPending} onClick={() => {
              form.reset();
              props.setOpen(false);
            }}>{"Cancel"}</Button><Button form="form" type="submit" onClick={e_0 => {
              e_0.preventDefault();
              e_0.stopPropagation();
              onSubmit();
            }} disabled={!form.formState.isValid || isSubmitting}>{"Submit"}</Button></DialogFooter></form></Form>}</DialogContent>;
    $[13] = form;
    $[14] = isSubmitting;
    $[15] = onSubmit;
    $[16] = props;
    $[17] = reportBugMutation.isPending;
    $[18] = showThankYou;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== handleOpenChange || $[21] !== props.open || $[22] !== t6) {
    t7 = <Dialog open={props.open} onOpenChange={handleOpenChange}>{t6}</Dialog>;
    $[20] = handleOpenChange;
    $[21] = props.open;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp4(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem><FormControl><div className="flex flex-row gap-2"><Checkbox checked={field_1.value} onCheckedChange={checked_0 => {
          field_1.onChange(checked_0 === true);
        }} /><div className="flex flex-row gap-1"><FormLabel>{"Allow admin access"}</FormLabel><Tooltip><TooltipTrigger><CircleHelp size={12} /></TooltipTrigger><TooltipContent><p>{"Enabling this allows admins to access the data for debugging purposes."}</p></TooltipContent></Tooltip></div></div></FormControl></FormItem>;
}
function _temp3(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormControl><div className="flex flex-row gap-2"><Checkbox checked={field_0.value} onCheckedChange={checked => {
          field_0.onChange(checked === true);
        }} /><FormLabel>{"Include screenshot"}</FormLabel></div></FormControl></FormItem>;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>{"Description / Steps to reproduce*"}</FormLabel><FormControl><Textarea className="mt-1" name="description" required={true} placeholder="Describe the issue and steps to reproduce." value={field.value} onChange={e => field.onChange(e.target.value)} /></FormControl></FormItem>;
}
async function _temp() {
  const dialog = document.querySelector("[role=\"dialog\"]");
  const overlay = document.querySelector("div[data-state=\"open\"].fixed");
  const scrollWidth = document.documentElement.scrollWidth;
  const scrollHeight = document.documentElement.scrollHeight;
  const canvas = await html2canvas(document.body, {
    scrollX: 0,
    scrollY: 0,
    width: scrollWidth,
    height: scrollHeight,
    windowWidth: scrollWidth,
    windowHeight: scrollHeight,
    scale: window.devicePixelRatio || 1,
    useCORS: true,
    ignoreElements: element => element === dialog || element === overlay
  });
  return canvas.toDataURL("image/png");
}